/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import ModalDialog from '@/io.ox/backbone/views/modal'
import { createApp } from 'vue'
import { useSignatureDesignerStore } from '@/io.ox/mail/settings/signatures/signature-designer/store/signature-designer-store'
import SignatureWizard from '@/io.ox/mail/settings/signatures/signature-designer/signature-designer-wizard.vue'

import yell from '@/io.ox/core/yell'
import gt from 'gettext'

export function openDialog () {
  const designerModal = new ModalDialog({
    className: 'modal flex io-ox-signature-designer',
    title: gt('Signature designer'),
    keepWhenPaused: true,
    autoClose: false
  })
    .build(function () {
      this.$header.hide()
      this.$footer.hide()
      this.$body.css('padding', 0)

      const vueApp = createApp(SignatureWizard, { close: () => designerModal.close(), save: () => onSave() })
      const mountedVueApp = vueApp.mount(this.el.querySelector('.modal-body'))

      this.on('close', mountedVueApp.onClose)

      // wait a bit for vue to render and update dom to avoid flickering
      setTimeout(() => this.open(), 100)
    })

  function onSave () {
    let insertion = 'below'
    const saveModal = new ModalDialog({
      className: 'modal flex',
      title: gt('Add signature'),
      autoClose: false
    })
      .build(function () {
        const formItem = document.createElement('div')
        formItem.className = 'form-group'

        const label = document.createElement('label')
        label.setAttribute('for', 'signature-name')
        label.textContent = gt('Signature name')

        const input = document.createElement('input')
        input.id = 'signature-name'
        input.name = 'name'
        input.className = 'form-control'

        const errorBlock = document.createElement('div')
        errorBlock.id = 'signature-name-error'
        errorBlock.className = 'help-block error'
        errorBlock.style.display = 'none'

        input.addEventListener('input', () => {
          if (input.value.trim() !== '') {
            input.classList.remove('error')
            input.removeAttribute('aria-invalid')
            errorBlock.textContent = ''
            errorBlock.style.display = 'none'
          }
        })

        formItem.append(label, input, errorBlock)

        // Signature position select
        const positionGroup = document.createElement('div')
        positionGroup.className = 'form-group'

        const positionLabel = document.createElement('label')
        positionLabel.setAttribute('for', 'signature-position')
        positionLabel.textContent = gt('Signature Position')

        const select = document.createElement('select')
        select.id = 'signature-position'
        select.className = 'form-control'

        const optionAbove = document.createElement('option')
        optionAbove.value = 'above'
        optionAbove.textContent = gt('Add signature above quoted text')

        const optionBelow = document.createElement('option')
        optionBelow.value = 'below'
        optionBelow.textContent = gt('Add signature below quoted text')

        select.append(optionAbove, optionBelow)
        select.value = insertion // default selection

        select.addEventListener('change', () => {
          insertion = select.value
        })

        positionGroup.append(positionLabel, select)

        this.$body[0].appendChild(formItem)
        this.$body[0].appendChild(positionGroup)
      })
      .addCancelButton()
      .addButton({ action: 'save', label: gt('Save') })
      .on('save', async () => {
        const input = document.getElementById('signature-name')
        const error = document.getElementById('signature-name-error')
        const displayname = input.value.trim()

        if (!displayname) {
          input.classList.add('error')
          input.setAttribute('aria-invalid', 'true')
          error.textContent = gt('Please enter a valid name')
          error.style.display = 'block'
          return
        }

        input.classList.remove('error')
        input.removeAttribute('aria-invalid')
        error.textContent = ''
        error.style.display = 'none'

        const store = useSignatureDesignerStore()
        const content = document.getElementById('signature-preview').outerHTML

        try {
          await store.createSignature({ content, displayname, insertion })
          designerModal.close()
          saveModal.close()
        } catch (error) {
          yell(error)
        } finally { store.resetContactData() }
      })
      .open()
  }
}
